import React, { useState, useEffect, useRef } from 'react';
import useModalContext from 'components/context/modal';
import useUserContext from 'ecommerce/context/user';
import syncApi from 'ecommerce/api/sync';
import useDepsInterval from 'components/hooks/useDepsInterval';
import pickExcept from 'utils/pickExcept';

import useEditorContext from '../../context/editorContext';
import { formatData } from 'editor-utils/dataParser';
import DraftSyncOutdated from '../components/draftSyncOutdated';

const useDraftSync = (editorItem, product, template) => {
  const userContext = useUserContext();
  const editorContext = useEditorContext();
  const modalContext = useModalContext();

  const [state, setState] = useState('');

  const saveIndex = useRef(0);
  const draftLastModifiedRef = useRef();

  const editorItemRef = useRef(editorItem);
  useEffect(() => {
    editorItemRef.current = editorItem;
  }, [editorItem]);

  const saveIsRequired = useRef(false);
  const connectionIsActive = useRef(false);

  const callback = async () => {
    let draftIsSuccessfullySynced = false;
    let draftIsAlreadyDeleted = false;

    // REAL DATA
    const item = editorItemRef.current;
    const formatedData = formatData(template, item);

    // User already has draftId
    // Update existing draft
    if (item.draftId) {
      if (draftLastModifiedRef.current) {
        if (draftLastModifiedRef.current !== item.draftLastModified) {
          console.error('sync_date_error', {
            ref: draftLastModifiedRef.current,
            context: item.draftLastModified,
            index: saveIndex.current,
          });
        }
      }

      const { data: syncData, error: syncError } = await syncApi(
        userContext.accessToken,
      ).updateProductDraft(
        item.draftId,
        product.id,
        template.templateUrl,
        template.version,
        formatedData,
        item.draftName,
        draftLastModifiedRef.current ?? item.draftLastModified,
      );

      if (syncError) {
        if (syncError.code === 404) {
          // current draft was not found
          draftIsAlreadyDeleted = true;
        } else if (syncError.lastModifiedOnServer) {
          if (syncError.serverHasSameData) {
            draftIsSuccessfullySynced = true;
            editorContext.setDraftLastModified(item.id, syncError.draftLastModifiedOnServer);
            draftLastModifiedRef.current = syncError.draftLastModifiedOnServer;
            saveIndex.current += 1;
          } else {
            modalContext.show(
              <DraftSyncOutdated
                accessToken={userContext.accessToken}
                lastModifiedOnClient={syncError.lastModifiedOnClient}
                lastModifiedOnServer={syncError.lastModifiedOnServer}
                item={item}
                template={template}
                data={formatedData}
              />,
              {
                title: 'Error Saving Project Draft',
                closeable: false,
              },
            );

            try {
              gtag('event', 'sync_error', {
                event_category: 'sync',
                event_label: item.draftId,
              });
            } catch (e) { }
          }

          console.error('sync_error', {
            lastModifiedOnClient: syncError.lastModifiedOnClient.toISOString(),
            lastModifiedOnServer: syncError.lastModifiedOnServer.toISOString(),
            // signatureOnClient: syncError.signatureOnClient,
            // signatureOnServer: syncError.signatureOnServer,
            diff: `${(syncError.lastModifiedOnClient - syncError.lastModifiedOnServer) / 1000}s`,
            saveIndex: saveIndex.current,
            serverHasSameData: syncError.serverHasSameData,
          });
        } else {
          console.log(
            `User #${userContext.data.id} can't update draft #${item.draftId} due to "${syncError.body}".`,
          );
          window.Rollbar?.error(
            `SyncError - "updateProductDraft" ${syncError.code} - ${syncError.body}`,
          );
        }
      } else {
        draftIsSuccessfullySynced = true;
        editorContext.setDraftLastModified(item.id, syncData.updated_at);
        draftLastModifiedRef.current = syncData.updated_at;
        saveIndex.current += 1;
      }
    }

    // User doesn't have draftId or draft was deleted before
    // Create new draft
    if (!item.draftId || draftIsAlreadyDeleted) {
      const { data: syncData, error: syncError } = await syncApi(
        editorContext.accessToken,
      ).saveProductDraft(product.id, template.templateUrl, template.version, formatedData);

      if (syncError) {
        console.log(
          `User #${userContext.data.id} can't create new draft due to "${syncError.body}".`,
        );
        window.Rollbar?.error(
          `SyncError - "saveProductDraft" ${syncError.code} - ${syncError.body}`,
        );
      } else {
        draftIsSuccessfullySynced = true;
        editorContext.setDraftId(item.id, syncData.id);
        editorContext.setDraftLastModified(item.id, syncData.updated_at);
        draftLastModifiedRef.current = syncData.updated_at;
      }
    }

    if (draftIsSuccessfullySynced) {
      return 'synced';
    }
    return 'error';
  };

  const save = async () => {
    saveIsRequired.current = true;

    while (!connectionIsActive.current && saveIsRequired.current) {
      setState('syncing');
      connectionIsActive.current = true;
      saveIsRequired.current = false;
      const result = await callback();
      setState(result);
      connectionIsActive.current = false;
    }
  };

  // SAVING
  useDepsInterval(
    () => {
      save();
    },
    5000,
    [pickExcept(editorItem, 'draftLastModified')],
    () => {
      setState('changed');
    },
  );

  return {
    state,
    save,
  };
};

export default useDraftSync;
